import { Link } from '@tanstack/react-router';
import { ChevronDown, Filter, Search } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button as AriaButton, Disclosure, DisclosurePanel, Heading } from 'react-aria-components';
import { partyApi } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queries';
import type { Party, TaxStatus } from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { parseDecimalInput } from '../../../lib/decimal';
import { NARROW_QUERY, PHONE_QUERY, useMediaQuery } from '../../../lib/hooks';
import { Button } from '../../../ui/Button';
import { Checkbox, CheckboxGroup } from '../../../ui/Choice';
import { AsyncComboBox } from '../../../ui/ComboBox';
import { cx } from '../../../ui/cx';
import { DateField } from '../../../ui/DatePicker';
import { DecimalField } from '../../../ui/DecimalField';
import { Sheet } from '../../../ui/Sheet';
import { criteriaOf } from '../batch/criteria';
import type { InvoiceListSearch } from '../InvoiceListPage';
import styles from '../Sales.module.css';
import { PRESET_VIEWS, activeView, facetCount, viewSearch, type ViewId } from './views';

export const TAX_STATUSES: TaxStatus[] = [
  'incomplete',
  'ready',
  'signing',
  'signed',
  'submitting',
  'submitted',
  'valid',
  'invalid',
  'rejected',
  'not_accepted',
  'cancelled',
];
export const DOC_STATUSES = ['draft', 'issued', 'discarded'] as const;

interface FacetDraft {
  status: string[];
  taxStatus: string[];
  from: string | null;
  to: string | null;
  customer: { id: string; name: string } | null;
  minAmount: string;
  maxAmount: string;
}

const EMPTY: FacetDraft = {
  status: [],
  taxStatus: [],
  from: null,
  to: null,
  customer: null,
  minAmount: '',
  maxAmount: '',
};

function draftOf(search: InvoiceListSearch): FacetDraft {
  const list = (v: string | undefined) => (v ?? '').split(',').filter(Boolean);
  return {
    status: list(search.status),
    taxStatus: list(search.taxStatus),
    from: search.from ?? null,
    to: search.to ?? null,
    customer: search.customer ? { id: search.customer, name: search.customerName ?? '' } : null,
    minAmount: search.minAmount ?? '',
    maxAmount: search.maxAmount ?? '',
  };
}

/** The URL patch of a facet draft, or null while an amount is malformed (nothing is applied). */
function patchOf(draft: FacetDraft): Partial<InvoiceListSearch> | null {
  const amount = (raw: string): string | undefined | null => {
    const parsed = parseDecimalInput(raw);
    if (parsed.kind === 'invalid') return null;
    return parsed.kind === 'valid' ? parsed.value : undefined;
  };
  const min = amount(draft.minAmount);
  const max = amount(draft.maxAmount);
  if (min === null || max === null) return null;
  // Canonical value order, so a view's definition matches whatever order the boxes were ticked in.
  const ordered = (all: readonly string[], picked: string[]) =>
    all.filter((v) => picked.includes(v)).join(',') || undefined;
  return {
    status: ordered(DOC_STATUSES, draft.status),
    taxStatus: ordered(TAX_STATUSES, draft.taxStatus),
    from: draft.from ?? undefined,
    to: draft.to ?? undefined,
    customer: draft.customer?.id,
    customerName: draft.customer?.name,
    minAmount: min,
    maxAmount: max,
  };
}

const APPLY_DELAY_MS = 300;

/**
 * The list's find panel (BLOCK-1-REDESIGN §A.1, DESIGN_SYSTEM §4 "Register pages"): search, the six
 * preset views, and the facets, which apply on change. At the inline start on desktop; a find bar
 * from 40 to 64rem (facets in a side sheet) and on phones (facets in a bottom sheet).
 */
export function FindPanel({
  orgId,
  search,
  onChange,
}: {
  orgId: string;
  search: InvoiceListSearch;
  /** Applies a patch to the URL search; the page goes back to the first. */
  onChange: (patch: Partial<InvoiceListSearch>) => void;
}) {
  const { t } = useI18n();
  const isNarrow = useMediaQuery(NARROW_QUERY);
  const isPhone = useMediaQuery(PHONE_QUERY);
  // D-21 (sales-notes.md §6, BLOCK-1B-BULK-DESIGN.md:61): the batch link stays OFF the note families
  // — bulk work is for invoices only — so it is hidden on the credit and debit tabs.
  const isNoteTab = search.tab === 'credit' || search.tab === 'debit';
  const count = facetCount(search);
  const view = activeView(search);
  // Collapsed unless the URL holds facets that match no view; afterwards it is the user's choice.
  const [expanded, setExpanded] = useState(() => count > 0 && view === null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const facetsLabel = count ? t.sales.filterCount(count) : t.sales.filter;
  const viewsTitle = useId();
  const batchTitle = useId();

  return (
    <aside
      className={cx(styles.findPanel, isNarrow && styles.findBar)}
      aria-label={t.sales.findLabel}
    >
      <SearchField value={search.q} onChange={(q) => onChange({ q })} />
      <nav className={styles.views} aria-labelledby={viewsTitle}>
        <h2 id={viewsTitle} className={styles.findHeading}>
          {t.sales.views}
        </h2>
        <ul className={styles.viewList}>
          {PRESET_VIEWS.map((preset) => (
            <li key={preset.id}>
              <Link
                to="/orgs/$orgId/sales/invoices"
                params={{ orgId }}
                search={(prev: InvoiceListSearch) => viewSearch(preset, prev)}
                replace
                // The router marks a link active only on an exact match; a view is also active on
                // later pages of its results.
                activeOptions={{ exact: true, includeSearch: true }}
                aria-current={view === preset.id ? 'page' : undefined}
                className={styles.viewLink}
                data-testid={`view-${preset.id}`}
              >
                {viewLabel(t, preset.id)}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      {isNarrow ? (
        <>
          <Button
            variant={count ? 'primary' : 'secondary'}
            icon={<Filter size={16} aria-hidden="true" />}
            aria-expanded={sheetOpen}
            onPress={() => setSheetOpen(true)}
            testId="filter-open"
            className={styles.facetsButton}
          >
            {facetsLabel}
          </Button>
          <Sheet
            isOpen={sheetOpen}
            onOpenChange={setSheetOpen}
            placement={isPhone ? 'bottom' : 'end'}
            title={t.sales.filterTitle}
            footer={
              isPhone ? (
                <Button
                  variant="primary"
                  className={styles.sheetDone}
                  onPress={() => setSheetOpen(false)}
                >
                  {t.sales.applyFilter}
                </Button>
              ) : undefined
            }
          >
            <Facets orgId={orgId} search={search} onApply={onChange} />
          </Sheet>
        </>
      ) : (
        <Disclosure
          isExpanded={expanded}
          onExpandedChange={setExpanded}
          className={styles.facetsDisclosure}
        >
          <Heading level={2} className={styles.findHeading}>
            <AriaButton slot="trigger" className={styles.facetsTrigger} data-testid="filter-open">
              <ChevronDown size={16} aria-hidden="true" className={styles.facetsChevron} />
              {facetsLabel}
            </AriaButton>
          </Heading>
          <DisclosurePanel>
            <Facets orgId={orgId} search={search} onApply={onChange} />
          </DisclosurePanel>
        </Disclosure>
      )}
      {isNoteTab ? null : isNarrow ? (
        <BatchEntryLink orgId={orgId} search={search} />
      ) : (
        <section
          className={styles.batchEntrySection}
          aria-labelledby={batchTitle}
          data-testid="batch-entry-section"
        >
          <h2 id={batchTitle} className={styles.findHeading}>
            {t.sales.batchEntry.title}
          </h2>
          <p className={styles.findHint}>{t.sales.batchEntry.hint}</p>
          <BatchEntryLink orgId={orgId} search={search} />
        </section>
      )}
    </aside>
  );
}

/**
 * The list's only entry to bulk work (WP-1b.3, BLOCK-1B-BULK-DESIGN BD-2, AC-B2): an underlined
 * text link inside the find panel's own block, after the views and the facets control, at every
 * width — never inside or beside the command bar. It navigates to the batch page with the list's
 * criteria (never the page or the page size); it is not a toggle, a mode or a state.
 */
function BatchEntryLink({ orgId, search }: { orgId: string; search: InvoiceListSearch }) {
  const { t } = useI18n();
  return (
    <Link
      to="/orgs/$orgId/sales/invoices/batch"
      params={{ orgId }}
      search={criteriaOf(search as Record<string, unknown>)}
      className={styles.batchEntryLink}
      data-testid="batch-entry"
    >
      {t.sales.batchEntry.link}
    </Link>
  );
}

function viewLabel(t: ReturnType<typeof useI18n>['t'], id: ViewId): string {
  switch (id) {
    case 'all':
      return t.sales.viewAll;
    case 'drafts':
      return t.sales.kpis.drafts;
    case 'sending':
      return t.sales.kpis.sending;
    case 'attention':
      return t.sales.kpis.attention;
    case 'valid':
      return t.sales.kpis.valid;
    case 'deleted':
      return t.sales.viewDeleted;
  }
}

/** The search box: number, buyer (Arabic-normalized on the server) or tax ID; 300 ms pause. */
function SearchField({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (q: string | undefined) => void;
}) {
  const { t } = useI18n();
  const id = useId();
  const hintId = useId();
  const [text, setText] = useState(value ?? '');
  useEffect(() => setText(value ?? ''), [value]);
  useEffect(() => {
    if (text === (value ?? '')) return;
    const timer = setTimeout(() => onChange(text || undefined), APPLY_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return (
    <div className={styles.findSearch}>
      <label htmlFor={id} className={styles.findHeading}>
        {t.sales.search}
      </label>
      <div className={styles.search}>
        <Search size={16} aria-hidden="true" />
        <input
          id={id}
          type="search"
          value={text}
          aria-describedby={hintId}
          onChange={(e) => setText(e.target.value)}
          data-testid="list-search"
        />
      </div>
      {/* A hint under the box, not a placeholder: it wraps instead of being cut (RI-07 b). */}
      <p id={hintId} className={styles.findHint}>
        {t.sales.searchPlaceholder}
      </p>
    </div>
  );
}

/** Document stage, ETA status, dates, buyer and amounts; each change applies after 300 ms. */
function Facets({
  orgId,
  search,
  onApply,
}: {
  orgId: string;
  search: InvoiceListSearch;
  onApply: (patch: Partial<InvoiceListSearch>) => void;
}) {
  const { t } = useI18n();
  const fromUrl = useMemo(
    () => draftOf(search),
    // Only the facet parameters matter here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      search.status,
      search.taxStatus,
      search.from,
      search.to,
      search.customer,
      search.customerName,
      search.minAmount,
      search.maxAmount,
    ],
  );
  const [draft, setDraft] = useState(fromUrl);
  const pending = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The URL changed elsewhere (a preset view, back and forward): follow it unless a change waits.
  useEffect(() => {
    if (!pending.current) setDraft(fromUrl);
  }, [fromUrl]);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const change = (next: FacetDraft) => {
    setDraft(next);
    pending.current = true;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      const patch = patchOf(next);
      if (!patch) return; // a malformed amount: its field says so; nothing is applied
      pending.current = false;
      onApply(patch);
    }, APPLY_DELAY_MS);
  };
  const clearAll = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    pending.current = false;
    setDraft(EMPTY);
    onApply(patchOf(EMPTY) ?? {});
  };

  return (
    <div className={styles.facets} data-testid="filter-panel">
      <CheckboxGroup
        label={t.sales.filterStatus}
        value={draft.status}
        onChange={(status) => change({ ...draft, status })}
        listClassName={styles.facetChecks}
      >
        {DOC_STATUSES.map((s) => (
          <Checkbox key={s} value={s} fieldName={`filter-status-${s}`}>
            {t.docStatus[s]}
          </Checkbox>
        ))}
      </CheckboxGroup>
      <CheckboxGroup
        label={t.sales.filterTaxStatus}
        value={draft.taxStatus}
        onChange={(taxStatus) => change({ ...draft, taxStatus })}
        listClassName={styles.facetChecks}
      >
        {TAX_STATUSES.map((s) => (
          <Checkbox key={s} value={s} fieldName={`filter-tax-${s}`}>
            {t.taxStatus[s]}
          </Checkbox>
        ))}
      </CheckboxGroup>
      <div className={styles.facetFields}>
        <DateField
          label={t.sales.filterFrom}
          value={draft.from}
          onChange={(from) => change({ ...draft, from })}
          fieldName="filter-from"
        />
        <DateField
          label={t.sales.filterTo}
          value={draft.to}
          onChange={(to) => change({ ...draft, to })}
          fieldName="filter-to"
        />
      </div>
      <AsyncComboBox<Party>
        label={t.sales.filterCustomer}
        fieldName="filter-customer"
        value={draft.customer?.id ?? null}
        selectedItem={
          draft.customer ? ({ id: draft.customer.id, name: draft.customer.name } as Party) : null
        }
        onChange={(p) => change({ ...draft, customer: p ? { id: p.id, name: p.name } : null })}
        queryFor={(q) => ({
          queryKey: [...queryKeys.org(orgId), 'parties', 'filter', q],
          queryFn: async ({ signal }) =>
            (await partyApi.list(orgId, { role: 'customer', q, limit: 20 }, signal)).items,
        })}
        getKey={(p) => p.id}
        getLabel={(p) => p.name}
        isClearable
      />
      <div className={styles.facetFields}>
        <DecimalField
          label={t.sales.filterMin}
          value={draft.minAmount}
          onChange={(minAmount) => change({ ...draft, minAmount })}
          fieldName="filter-min"
        />
        <DecimalField
          label={t.sales.filterMax}
          value={draft.maxAmount}
          onChange={(maxAmount) => change({ ...draft, maxAmount })}
          fieldName="filter-max"
        />
      </div>
      <Button
        variant="ghost"
        size="sm"
        onPress={clearAll}
        className={`${styles.textButton} ${styles.clearFacets}`}
      >
        {t.sales.clearFilter}
      </Button>
    </div>
  );
}
